import { WITHDRAWAL_PROMO_CODE } from '@inyova/mocks';
import {
  Account,
  AccountInvestDetails,
  AccountKind,
  AccountTransactions,
  Customer,
  CustomerAccountItem,
  FeatureFlags,
  InyovaGrowAccountStatus,
  LoadState,
  Promotions,
  PromotionVm,
  StaticDocument,
  TransactionsRangeFilters,
  TransactionsTypeFilters,
  ViewItems
} from '@inyova/models';
import { getTransactionsRangeEndDate, isMinProspect } from '@inyova/utils';
import * as dayjs from 'dayjs';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as AccountActions from './account.actions';
import * as RootActions from '@app/app.actions';
import { selectSelectedAccount, selectCustomerViewedItem, selectViewedItemsLoadState } from '@app/app.reducers';

import { MAXIMUM_3A_CONTRIBUTION } from '@app/app.constants';
import {
  ABTest,
  AccountDocument,
  Address,
  Documents3AStore,
  FinancialSituation,
  GrowDocument,
  ProfileBannerItem,
  Transaction,
  TransactionType
} from '@shared/models/Account';
import { State } from '@shared/models/State';

export const ACCOUNT_REDUCER_FEATURE_KEY = 'account';

export const initialState: AccountState = {
  switchAccountLoading: {
    show: false,
    message: ''
  },
  address: {
    city: '',
    country: '',
    number: '',
    street: '',
    zip: ''
  },
  customer: {
    interview_widget: false,
    inyova_pro: false,
    superhuman_visible: false,
    accounts: [],
    temporary_accounts: [],
    total_current_amount: 0,
    app_location: 'ch',
    created_at: '',
    email: '',
    email_confirmed: false,
    enabled_features: [],
    first_name: '',
    id: 0,
    inyova_id: 0,
    language: 'de-CH',
    last_name: '',
    lead_source: '',
    otp_module: 'disabled',
    phone: '',
    verified_phone: '',
    unverified_phone: '',
    provider: '',
    source_type_id: 3,
    tag_manager: { origin: '', first_time: false },
    is_friend: false,
    is_test: false,
    referral_code: '',
    hide_personalisation: false,
    total_referrals: {
      free_feedays: 0,
      my_referral_customer: '',
      referrallimit: false
    },
    test_version: '',
    track_id: '',
    uid: '',
    has_crowd_investor_account: false,
    has_interest_account: false,
    saxo_data_renewal_expires_at: null,
    saxo_bank_onboarding_done: false,
    show_eve2_opt_in: null,
    show_interest_account_banner: false,
    show_greenness_survey: null,
    qualification_step: null
  },
  currentAccount: {
    id: '',
    owner_name: '',
    step_status: 0,
    onboarding_step: 0,
    kind: '3b',
    edit_mode: false,
    enabled_features: [],
    strategy_edit_mode_disabled_at: null,
    strategy_edit_mode_enabled_at: null,
    on_hold: false,
    active: true,
    created_at: '',
    initial_investment: 0,
    current_amount: 0,
    currency: 'CHF',
    stock_split: 0,
    customer_journey: {
      account_data_filled: true,
      account_opened: true,
      account_funded: true,
      customer_funded: true,
      compliance_done: true,
      id_verification_completed: true,
      risk_checked: true,
      ql_flow_completed: true
    },
    iban: '',
    is_yova_yellow: false,
    show_eve2_explanation_modal: false,
    max_contribution_level: 'small',
    minimum_investment: 0,
    deposit_qr_code: '',
    deposit_this_year: 0,
    liberty_account: {
      qr_iban: '',
      reference_number: ''
    },
    savings_plan_social_proof: null,
    liquidation_only: null,
    has_low_investment_balance: false,
    next_strategy_change_on: '',
    inyova_fee: 0,
    ql_onboarding_step: 0,
    risk_recheck_visible: false,
    tutorial_seen: false
  },
  customerDocuments: {
    initialized: false,
    documents: []
  },
  documents3A: {
    initialized: false,
    documents: [],
    pagination: {
      total: 0
    }
  },
  documentsGrow: {
    initialized: false,
    documents: []
  },
  staticDocuments: {
    initialized: false,
    documents: []
  },
  referrals: {
    loadState: 'INIT',
    promotions: {
      data: [],
      included: [],
      meta: {
        active_rewards: {
          trees_planted: 0,
          months: 0,
          money_monthly: 0
        },
        pending_rewards: {
          months: 0,
          money_overall: 0,
          funding_months: 0,
          funding_money: 0,
          commitment_months: 0,
          commitment_money: 0
        },
        aggregated_money_rewards: {
          customer_count: 0,
          money_value: 0
        },
        referral_rewards: {
          months: 0,
          money: {
            money_monthly: 0,
            months_duration: 0
          }
        },
        referrer_code: ''
      }
    }
  },
  accountTransactions: {
    loadState: 'INIT',
    transactions: [],
    count: 10,
    rangeFilter: TransactionsRangeFilters.ALL,
    selectedTypeFilters: [],
    customRange: {
      from: null,
      to: null
    }
  },
  ABTests: [],
  profileBanners: [],
  financialSituation: null,
  deEve2OptInLoadState: 'INIT'
};

const accountReducer = createReducer(
  initialState,
  on(AccountActions.getCustomerDocumentsSuccess, (state, action): AccountState => {
    return {
      ...state,
      customerDocuments: {
        initialized: true,
        documents: action.documents
      }
    };
  }),
  on(AccountActions.getCustomerDocumentsFail, (state): AccountState => {
    return {
      ...state,
      customerDocuments: {
        initialized: true,
        documents: []
      }
    };
  }),
  on(
    AccountActions.getDocuments3ASuccess,
    (state, action): AccountState => ({
      ...state,
      documents3A: {
        ...state.documents3A,
        initialized: true,
        documents: [...state.documents3A.documents, ...action.documents],
        pagination: { total: action.meta.stats?.total.count ?? 0 }
      }
    })
  ),
  on(
    AccountActions.getDocuments3AFail,
    (state): AccountState => ({
      ...state,
      documents3A: {
        ...state.documents3A,
        initialized: true,
        documents: []
      }
    })
  ),
  on(
    AccountActions.getStaticDocumentsSuccess,
    (state, action): AccountState => ({
      ...state,
      staticDocuments: {
        initialized: true,
        documents: action.documents
      }
    })
  ),
  on(
    AccountActions.getStaticDocumentsFail,
    (state): AccountState => ({
      ...state,
      staticDocuments: {
        initialized: true,
        documents: []
      }
    })
  ),
  on(AccountActions.getDocumentsGrowSuccess, (state, action): AccountState => {
    return {
      ...state,
      documentsGrow: {
        initialized: true,
        documents: action.documents
      }
    };
  }),
  on(AccountActions.getDocumentsGrowFail, (state): AccountState => {
    return {
      ...state,
      documentsGrow: {
        initialized: true,
        documents: []
      }
    };
  }),
  on(AccountActions.transactionsPageOpened, (state): AccountState => {
    return state.accountTransactions.loadState === 'LOADED'
      ? state
      : { ...state, accountTransactions: { ...state.accountTransactions, loadState: 'LOADING' } };
  }),
  on(
    AccountActions.getAccountTransactionsSuccess,
    (state, { transactions }): AccountState => ({
      ...state,
      accountTransactions: { ...state.accountTransactions, loadState: 'LOADED', transactions }
    })
  ),
  on(
    AccountActions.getAccountTransactionsFail,
    (state): AccountState => ({
      ...state,
      accountTransactions: { ...state.accountTransactions, loadState: { error: 'Something went wrong' } }
    })
  ),
  on(AccountActions.applyTransactionsFilters, (state, { rangeFilter, selectedTypeFilters, customRange }) => ({
    ...state,
    accountTransactions: { ...state.accountTransactions, rangeFilter, selectedTypeFilters, customRange, count: 10 }
  })),
  on(AccountActions.showMoreTransactions, (state) => ({
    ...state,
    accountTransactions: { ...state.accountTransactions, count: state.accountTransactions.count + 10 }
  })),
  on(AccountActions.referralsPageOpened, AccountActions.getPromotions, (state, { forced }: { forced: boolean }): AccountState => {
    return state.referrals.loadState === 'LOADED' && !forced ? state : { ...state, referrals: { ...state.referrals, loadState: 'LOADING' } };
  }),
  on(
    AccountActions.getPromotionsSuccess,
    (state, { data }): AccountState => ({
      ...state,
      referrals: { ...state.referrals, loadState: 'LOADED', promotions: data }
    })
  ),
  on(
    AccountActions.getPromotionsFail,
    (state): AccountState => ({
      ...state,
      referrals: { ...state.referrals, loadState: 'LOADING' }
    })
  ),
  on(
    AccountActions.redeemPromocode,
    (state): AccountState => ({
      ...state,
      referrals: { ...state.referrals, loadState: 'INIT' }
    })
  ),
  on(
    AccountActions.setCurrentAccount,
    AccountActions.getAccountAfterGrowRiskUpdateSuccess,
    (state, action): AccountState => ({
      ...state,
      currentAccount: action.data
    })
  ),
  on(
    AccountActions.setAccountDetails,
    (state, action): AccountState => ({
      ...state,
      address: {
        city: action.data.city,
        country: action.data.country,
        number: action.data.number,
        street: action.data.street,
        zip: action.data.zip
      }
    })
  ),
  // Remove app_location definition, after API starts return actual location
  on(
    AccountActions.setCustomer,
    (state, action): AccountState => ({
      ...state,
      customer: action.data
    })
  ),
  on(
    AccountActions.setSwitchAccountLoading,
    (state, action): AccountState => ({
      ...state,
      switchAccountLoading: {
        show: action.show,
        message: action.message
      }
    })
  ),
  on(
    AccountActions.changeLanguage,
    RootActions.setSelectedAccount,
    (state): AccountState => ({
      ...state,
      accountTransactions: {
        ...initialState.accountTransactions
      }
    })
  ),
  on(
    AccountActions.getABTestsSuccess,
    (state, action): AccountState => ({
      ...state,
      ABTests: action.data
    })
  ),
  on(
    AccountActions.getProfileBannersSuccess,
    (state, action): AccountState => ({
      ...state,
      profileBanners: action.data
    })
  ),
  on(
    AccountActions.getProfileBannersFail,
    (state): AccountState => ({
      ...state,
      profileBanners: []
    })
  ),
  on(
    AccountActions.getFinancialSituationSuccess,
    (state, action): AccountState => ({
      ...state,
      financialSituation: action.data
    })
  ),
  on(
    AccountActions.getFinancialSituationFail,
    (state): AccountState => ({
      ...state,
      financialSituation: null
    })
  ),
  on(AccountActions.getFinancialDetailsSuccess, (state, { financialSituation }) => ({
    ...state,
    financialSituation
  })),
  on(AccountActions.getFinancialDetailsFail, (state) => ({
    ...state,
    financialSituation: null
  })),
  on(AccountActions.setInyovaGrowAccountFlags, (state, action): AccountState => {
    const interestAccount: CustomerAccountItem | undefined = state.customer.temporary_accounts.find(
      (accountItem) => accountItem.id === action.growAccountId
    );

    let temporary_accounts = [...state.customer.temporary_accounts];

    if (typeof interestAccount === 'undefined') {
      temporary_accounts = state.customer.temporary_accounts.concat([
        {
          id: action.growAccountId,
          kind: 'grow',
          owner_name: '',
          account_currency: 'CHF',
          step_status: InyovaGrowAccountStatus.OPENED
        } as CustomerAccountItem
      ]);
    } else {
      if (interestAccount.step_status === 1) {
        temporary_accounts = state.customer.temporary_accounts.map((account) => {
          if (account.id === interestAccount.id) {
            return {
              ...account,
              step_status: 4
            };
          } else {
            return account;
          }
        });
      }
    }

    return {
      ...state,
      customer: {
        ...state.customer,
        has_interest_account: action.has_interest_account,
        show_interest_account_banner: action.show_interest_account_banner,
        temporary_accounts
      }
    };
  }),
  on(AccountActions.addGrowItemIntoTemporaryList, (state, { account }) => {
    if (state.customer.temporary_accounts.find((item) => item.id === account.id)) {
      return state;
    }
    return {
      ...state,
      customer: {
        ...state.customer,
        temporary_accounts: state.customer.temporary_accounts.concat([account])
      }
    };
  }),
  on(AccountActions.submitGreennessSurveySuccess, (state) => ({
    ...state,
    customer: {
      ...state.customer,
      show_greenness_survey: null
    }
  })),
  on(AccountActions.submitEve2OptIn, (state) => ({
    ...state,
    deEve2OptInLoadState: 'LOADING' as LoadState
  })),
  on(AccountActions.submitEve2OptInSuccess, (state) => ({
    ...state,
    customer: {
      ...state.customer,
      show_eve2_opt_in: false
    },
    deEve2OptInLoadState: 'LOADED' as LoadState
  })),
  on(AccountActions.submitEve2OptInFail, AccountActions.hideEve2OptInSuccessModal, (state) => ({
    ...state,
    deEve2OptInLoadState: 'INIT' as LoadState
  })),
  // NgRx store clean up after logout
  on(AccountActions.customerLogout, (): AccountState => ({ ...initialState })),
  on(AccountActions.customerLogoutAndUnpairDevice, (): AccountState => ({ ...initialState }))
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function reducer(state: AccountState | undefined, action: Action) {
  return accountReducer(state, action);
}

export interface AccountState {
  switchAccountLoading: {
    show: boolean;
    message: string;
  };
  address: Address;
  customer: Customer;
  currentAccount: Account;
  customerDocuments: {
    initialized: boolean;
    documents: AccountDocument[];
  };
  documents3A: Documents3AStore;
  documentsGrow: {
    initialized: boolean;
    documents: GrowDocument[];
  };
  staticDocuments: {
    initialized: boolean;
    documents: StaticDocument[];
  };
  referrals: {
    loadState: LoadState;
    promotions: Promotions;
  };
  accountTransactions: {
    loadState: LoadState;
    transactions: Transaction[];
    count: number;
    rangeFilter: TransactionsRangeFilters;
    selectedTypeFilters: TransactionsTypeFilters[];
    customRange: {
      from: null | string;
      to: null | string;
    };
  };
  ABTests: ABTest[];
  profileBanners: ProfileBannerItem[];
  financialSituation: FinancialSituation;
  deEve2OptInLoadState: LoadState;
}

/*
 * SELECTORS
 * */
export const selectFeature = (state: State) => state.account;
// Account reducer selectors
export const selectCustomer = createSelector(selectFeature, (state: AccountState) =>
  state && state.customer ? state.customer : initialState.customer
);
export const selectHas3AAccountMinProspect = createSelector(selectCustomer, (state: Customer) =>
  state.accounts.some((item) => item.kind === '3a' && isMinProspect(item))
);
export const selectCurrentAccount = createSelector(
  selectFeature,
  selectSelectedAccount,
  (state: AccountState, selectedAccount: CustomerAccountItem) => {
    if (!state || state.currentAccount.id !== selectedAccount?.id) {
      return initialState.currentAccount;
    }
    return state.currentAccount;
  }
);

export const selectGrowAccount = createSelector(selectFeature, (state: AccountState) => {
  if (!state || state.customer.temporary_accounts.length === 0) {
    return undefined;
  }
  return state.customer.temporary_accounts.find(({ kind }) => kind === 'grow');
});

export const selectCustomerAndCurrentAccount = createSelector(selectCustomer, selectCurrentAccount, (customer, currentAccount) => {
  return { customer, currentAccount };
});

export const selectAccountFeatureFlag = (flag: FeatureFlags) =>
  createSelector(selectCustomerAndCurrentAccount, selectSelectedAccount, ({ customer, currentAccount }, selectedAccount: CustomerAccountItem) => {
    const currentAccountId = currentAccount.id !== '' ? currentAccount.id : selectedAccount.id;
    return customer.temporary_accounts.find((account) => account.id === currentAccountId)?.enabled_features.includes(flag);
  });

export const selectCustomerFeatureFlag = (flag: FeatureFlags) =>
  createSelector(selectCustomer, (customer) => {
    return customer.enabled_features.includes(flag);
  });

export const selectShowInterestAccountBanner = createSelector(selectCustomer, (customer) => {
  return customer.show_interest_account_banner;
});

export const selectDocuments3A = createSelector(selectFeature, (state: AccountState) => state.documents3A);
export const selectDocuments3APaged = createSelector(selectDocuments3A, (state: Documents3AStore) => {
  if (!state || !state.documents || !state.documents.length) {
    return {
      documents: [],
      totalNumber: 0,
      initialized: state.initialized
    };
  }

  return {
    documents: state.documents,
    totalNumber: state.pagination.total,
    initialized: state.initialized
  };
});

export const selectGeneralDocuments = createSelector(selectFeature, (state: AccountState) => {
  const { initialized, documents } = state.customerDocuments;

  const accountSpecificDocIDs = [5, 22, 23, 24, 42, 43];
  const docs = documents
    .filter((doc) => !accountSpecificDocIDs.includes(doc.attributes.type_id))
    .sort((a, b) => b.attributes.type_id - a.attributes.type_id);
  return { initialized, docs };
});

export const selectDocumentsCI = createSelector(selectFeature, (state: AccountState) => {
  const { initialized, documents } = state.customerDocuments;

  const docs = documents.filter((doc) => [42, 43].includes(doc.attributes.type_id)).sort((a, b) => a.attributes.type_id - b.attributes.type_id);

  return { initialized, docs };
});

export const selectDocuments3B = createSelector(selectFeature, (state: AccountState) => {
  const { initialized } = state.customerDocuments;

  const reportingDocs = [];
  const taxDocs = [];

  state.customerDocuments.documents.forEach((doc) => {
    if (doc.attributes.type_id === 22) {
      reportingDocs.push(doc);
    } else if (doc.attributes.type_id === 5) {
      taxDocs.push(doc);
    }
  });

  return { initialized, taxDocs, reportingDocs };
});

export const selectDocumentsGrow = createSelector(selectFeature, (state: AccountState) => {
  const { initialized, documents } = state.documentsGrow;

  const groupedDocuments: { [key: string]: { title: string; documents: GrowDocument[] } } = documents.reduce(function (
    obj: { [key: string]: { title: string; documents: GrowDocument[] } },
    document
  ) {
    if (typeof obj[document.project_id] === 'undefined') {
      obj[document.project_id] = { title: document.project_title, documents: [document] };
      return obj;
    }
    obj[document.project_id].documents.push(document);
    return obj;
  }, {});

  const projects = Object.keys(groupedDocuments).map((key) => ({
    title: groupedDocuments[key].title,
    documents: groupedDocuments[key].documents
  }));
  return { initialized, projects };
});

export const selectTransfer3ADocument = createSelector(selectFeature, (state: AccountState) => {
  if (!state || !state.customerDocuments || !state.customerDocuments.initialized) return [];
  return state.customerDocuments.documents.filter((doc) => doc.attributes.type_id === 24);
});

export const selectStaticDocuments = createSelector(selectFeature, (state: AccountState) => {
  const { initialized } = state.staticDocuments;
  const docs = state.staticDocuments.documents;

  let static3aDocs: StaticDocument[] = [];
  let staticGeneralDocs: StaticDocument[] = [];
  let agmDocs: StaticDocument[] = [];
  let egmDocs: StaticDocument[] = [];
  let CIUpdates: StaticDocument[] = [];

  for (const doc of docs) {
    switch (doc.attributes.account_type) {
      case 'crowd_investing':
        switch (doc.attributes.category) {
          case 'agm':
            agmDocs = [...agmDocs, doc];
            break;
          case 'egm':
            egmDocs = [...egmDocs, doc];
            break;
          case 'updates':
            CIUpdates = [...CIUpdates, doc];
            break;
          default:
            break;
        }
        break;
      case '3a':
        static3aDocs = [...static3aDocs, doc];
        break;
      case '3b':
      case 'grow':
        staticGeneralDocs = [...staticGeneralDocs, doc];
        break;
      default:
        break;
    }
  }

  return { initialized, CIUpdates, agmDocs, egmDocs, static3aDocs, staticGeneralDocs };
});

export const selectAllDocuments = createSelector(
  selectGeneralDocuments,
  selectDocuments3B,
  selectDocumentsGrow,
  selectDocuments3APaged,
  selectStaticDocuments,
  selectDocumentsCI,
  (docsGeneral, docs3b, docsGrow, docs3a, docsStatic, docsCI) => {
    return {
      docsGeneral,
      docs3b,
      docsGrow,
      docs3a,
      docsStatic,
      docsCI
    };
  }
);

export const selectAccountTransactionsState = createSelector(selectFeature, (state: AccountState) => state.accountTransactions);

export const selectAccountTransactions = createSelector(selectAccountTransactionsState, (accountTransactions) => accountTransactions.transactions);
export const selectAccountTransactionsLoadState = createSelector(
  selectAccountTransactionsState,
  (accountTransactions) => accountTransactions.loadState
);

export const selectAccountTransactionsFilters = createSelector(
  selectAccountTransactionsState,
  ({ rangeFilter, selectedTypeFilters, customRange }) => {
    return { rangeFilter, selectedTypeFilters, customRange };
  }
);

export const selectAccountTransactionsCount = createSelector(selectAccountTransactionsState, ({ count }) => count);

export const selectFilteredAccountTransactions = createSelector(
  selectAccountTransactions,
  selectAccountTransactionsFilters,
  (transactions, { rangeFilter, selectedTypeFilters, customRange }) => {
    const isTypeAll = selectedTypeFilters.length === 0;
    const isTypeMatch = (transaction: Transaction) =>
      isTypeAll || selectedTypeFilters.includes(transaction.transaction_type.toLowerCase() as TransactionsTypeFilters);

    // no filter selected - Return all transactions || type filter is not all - filter by transaction type
    if (rangeFilter === TransactionsRangeFilters.ALL) {
      return isTypeAll ? transactions : transactions.filter(isTypeMatch);
    }

    if (rangeFilter !== TransactionsRangeFilters.CUSTOM_RANGE) {
      const pickedDate = getTransactionsRangeEndDate(rangeFilter);
      const filtered: Transaction[] = [];

      for (const transaction of transactions) {
        if (transaction.transaction_date < pickedDate) {
          break;
        }
        if (isTypeMatch(transaction)) {
          filtered.push(transaction);
        }
      }
      return filtered;
    }

    const { from, to } = customRange;
    const filtered: Transaction[] = [];

    for (const transaction of transactions) {
      if (transaction.transaction_date < from) {
        break;
      }
      if (transaction.transaction_date <= to && isTypeMatch(transaction)) {
        filtered.push(transaction);
      }
    }
    return filtered;
  }
);

export const selectFilteredAccountTransactionsLength = createSelector(selectFilteredAccountTransactions, (transactions) => {
  return transactions.length;
});

export const selectAccountTransactionsSlice = createSelector(
  selectAccountTransactionsCount,
  selectFilteredAccountTransactions,
  (count, transactions) => {
    return transactions.slice(0, count);
  }
);

export const selectShowInviteInsideTransactions = createSelector(
  selectFilteredAccountTransactions,
  selectAccountTransactionsFilters,
  (transactions, { selectedTypeFilters }): { showInvite: boolean; invitePosition: null | number } => {
    const isAllTypesSelected = selectedTypeFilters.length === 0;
    const includesInterest = selectedTypeFilters.includes(TransactionsTypeFilters.INTEREST);
    if ((!isAllTypesSelected && !includesInterest) || transactions.length === 0) {
      return { showInvite: false, invitePosition: null };
    }

    let interestTransactionsSum = 0;
    let firstCouponIndex = -1;

    for (const [index, transaction] of transactions.entries()) {
      if (transaction.transaction_type === TransactionType.COUPON) {
        interestTransactionsSum += transaction.transaction_value;
        if (firstCouponIndex === -1) {
          firstCouponIndex = index;
        }
      }
      if (interestTransactionsSum >= 100) {
        break;
      }
    }

    return {
      showInvite: interestTransactionsSum >= 100,
      invitePosition: interestTransactionsSum >= 100 ? firstCouponIndex : null
    };
  }
);

export const selectTransactionsSummary = createSelector(
  selectAccountTransactionsFilters,
  selectFilteredAccountTransactions,
  ({ selectedTypeFilters, rangeFilter }, transactions: Transaction[]) => {
    const isAllTypesSelected = selectedTypeFilters.length === 0;
    const summaryData: { [key: string]: { sum: number; transactionsCount: number; accruedInterest?: number } } = {};
    let totalSum = 0;

    if (rangeFilter === TransactionsRangeFilters.ALL && isAllTypesSelected) {
      return { summaryData, totalSum };
    }

    for (const transaction of transactions) {
      const transactionType = transaction.transaction_type.toLowerCase();
      totalSum += transaction.transaction_value;

      if (summaryData[transactionType]) {
        summaryData[transactionType] = {
          sum: summaryData[transactionType].sum + transaction.transaction_value,
          transactionsCount: summaryData[transactionType].transactionsCount + 1,
          ...(transaction.transaction_type === TransactionType.BUY && {
            accruedInterest: (summaryData[transactionType].accruedInterest ?? 0) + (transaction.accrued_interest_at_purchase ?? 0)
          })
        };
      } else {
        summaryData[transactionType] = {
          sum: transaction.transaction_value,
          transactionsCount: 1,
          ...(transaction.transaction_type === TransactionType.BUY && {
            accruedInterest: transaction.accrued_interest_at_purchase ?? 0
          })
        };
      }
    }
    return { summaryData, totalSum };
  }
);

export const selectAccountTransactionsHasAtLeastSingleInterestPayment = createSelector(
  selectCurrentAccount,
  selectAccountTransactionsLoadState,
  selectAccountTransactionsFilters,
  selectFilteredAccountTransactions,
  (account: Account, loadState: LoadState, { selectedTypeFilters }, transactions) => {
    const isAllTypesSelected = selectedTypeFilters.length === 0;
    const includesInterest = selectedTypeFilters.includes(TransactionsTypeFilters.INTEREST);
    if (account.kind !== 'grow' || loadState !== 'LOADED' || (!isAllTypesSelected && !includesInterest)) {
      return false;
    }
    return transactions.some((transaction) => transaction.transaction_type === TransactionType.COUPON);
  }
);

export const selectAccountTransactionsShowReferralModal = createSelector(
  selectAccountTransactionsHasAtLeastSingleInterestPayment,
  selectCustomerViewedItem(ViewItems.GROW_FIRST_EARNING_INVITE),
  selectViewedItemsLoadState,
  (hasAtLeastSingleInterestPayment, customerViewedItem, loadState) => {
    return loadState === 'LOADED' && !customerViewedItem && hasAtLeastSingleInterestPayment;
  }
);

export const selectAccountTransactionsView = createSelector(
  selectCurrentAccount,
  selectAccountTransactionsLoadState,
  selectAccountTransactionsSlice,
  selectFilteredAccountTransactionsLength,
  selectAccountTransactionsFilters,
  selectTransactionsSummary,
  selectShowInviteInsideTransactions,
  selectAccountTransactionsShowReferralModal,
  (
    account: Account,
    loadState: LoadState,
    transactions,
    transactionsTotalLength: number,
    { rangeFilter, selectedTypeFilters, customRange },
    { summaryData, totalSum },
    { showInvite, invitePosition },
    showReferralModal
  ): AccountTransactions => {
    return {
      kind: account.kind as AccountKind,
      currency: account.currency,
      loadState,
      transactions,
      transactionsTotalLength,
      rangeFilter,
      selectedTypeFilters,
      customRange,
      summaryData,
      totalSum,
      showInvite,
      invitePosition,
      hasActiveFilter: rangeFilter !== TransactionsRangeFilters.ALL || selectedTypeFilters.length > 0,
      ...(account.kind === 'kid' && { kidName: account.owner_name }),
      showReferralModal
    };
  }
);

// Invite friends, promotions and referrals
export const selectReferrals = createSelector(selectFeature, (state) => state.referrals);
export const selectPromotionsLoadState = createSelector(selectReferrals, (referrals) => referrals.loadState);
export const selectPromotions = createSelector(selectReferrals, (referrals) => referrals.promotions);
export const selectPromotionsDataForRedeemCode = createSelector(selectPromotions, (state: Promotions) => {
  const defaults = {
    code: '',
    activatedAt: '',
    freeMonths: 0,
    moneyPrmotion: {
      amount: 0,
      period: 0
    }
  };

  if (!state.included || !state.included?.length) {
    return defaults;
  }

  const attributes = state.included[0]?.attributes;

  defaults.code = attributes.code;
  defaults.activatedAt = state.data[0]?.attributes?.valid_from;

  if (attributes.type === 'MoneyPromotion') {
    defaults.moneyPrmotion.amount = attributes.value;
    defaults.moneyPrmotion.period = Number(attributes.months_duration);
  }

  if (attributes.type === 'FreeMonthsPromotion') {
    defaults.freeMonths = Number(attributes.months_duration);
  }

  return defaults;
});

export const selectWithdrawalPromotion = createSelector(selectPromotions, (promotions: Promotions) => {
  if (!promotions || !promotions.included || promotions.included.length === 0) {
    return false;
  }

  const notForReferMonthsSum = promotions.included
    .filter((promotion) => promotion.attributes.for_referring === false)
    .reduce((acc, next) => {
      return acc + parseInt(next.attributes.months_duration);
    }, 0);

  if (notForReferMonthsSum > 9) {
    // do not show WDL promo step
    return true;
  }

  return !!promotions.included.find((promotion) => promotion.attributes.code === WITHDRAWAL_PROMO_CODE);
});

export const selectNeonPromotion = createSelector(selectPromotions, (state: Promotions) => {
  if (!state || !state.included || state.included.length === 0) {
    return false;
  }
  const neonPromotion = state.included.find((promotion) => promotion.attributes.code === 'neon3a');
  if (!neonPromotion) {
    return false;
  }
  const neonValidityDate = state.data.find((promotion) => Number(promotion.relationships.promotion.data.id) === Number(neonPromotion.id)).attributes
    .valid_from;
  const dayDifference = dayjs().diff(dayjs(neonValidityDate, 'YYYY-MM-DD'), 'day');
  return dayDifference < 60;
});

export const selectMultipleActivePromotions = createSelector(
  selectPromotions,
  selectCurrentAccount,
  selectNeonPromotion,
  (state: Promotions, currentAccount, neonPromotionActive) => {
    if (!state || !state.meta || !state.meta.active_rewards) {
      return false;
    }

    const multipleActivePromotions = Object.keys(state.meta.active_rewards).filter((key) => state.meta.active_rewards[key] !== 0).length;

    return multipleActivePromotions > 1 || (multipleActivePromotions === 1 && neonPromotionActive && currentAccount.kind === '3a');
  }
);

export const selectNoActiveRewards = createSelector(selectPromotions, selectNeonPromotion, ({ meta: { active_rewards } }, hasNeonPromotion) => {
  return !(active_rewards.months || active_rewards.money_monthly || hasNeonPromotion);
});

export const selectRewardsCurrency = createSelector(selectCustomerAndCurrentAccount, ({ customer, currentAccount }) => {
  const acc3b: CustomerAccountItem | undefined = customer.temporary_accounts.find((account) => account.kind === '3b');
  if (typeof acc3b !== 'undefined') {
    return acc3b.account_currency;
  }
  return currentAccount.currency;
});

export const selectReferralsPageVm = createSelector(
  selectPromotionsLoadState,
  selectCustomer,
  selectRewardsCurrency,
  selectNeonPromotion,
  selectPromotions,
  selectNoActiveRewards,
  (
    loadState,
    customer,
    currency,
    hasNeonPromotion,
    { meta: { active_rewards, pending_rewards, referral_rewards, aggregated_money_rewards } },
    noActiveRewards
  ) => {
    return {
      loadState,
      hasNeonPromotion,
      promo: {
        activeMonths: active_rewards.months,
        activeMoneyMonthly: active_rewards.money_monthly,
        people: aggregated_money_rewards.customer_count,
        earnedAmount: aggregated_money_rewards.money_value,
        pendingMoneyOverall: pending_rewards.money_overall,
        pendingMonths: pending_rewards.months,
        referralMoneyMonthsDuration: referral_rewards.money !== null ? referral_rewards.money.months_duration : null,
        referralMoneyMoneyMonthly: referral_rewards.money !== null ? referral_rewards.money.money_monthly : null,
        referralMonths: referral_rewards.months
      },
      currency,
      referralCode: customer.referral_code,
      appLocation: customer.app_location,
      language: customer.language,
      showRedeemLink: false,
      noActiveRewards
    } as PromotionVm;
  }
);

export const selectAccountDetails = createSelector(selectFeature, (state: AccountState) => {
  if (!state) return initialState.address;
  return state.address;
});
export const selectIsAccountLoading = createSelector(selectFeature, (state: AccountState) => {
  if (!state) return initialState.switchAccountLoading;
  return state.switchAccountLoading;
});

export const selectActiveABTest = (experimentID: string) =>
  createSelector(selectFeature, (state: AccountState) => {
    const concreteABTest = state.ABTests.find((element) => element.attributes.experiment_id === experimentID);
    return concreteABTest?.attributes.alternative === 'false' ? false : concreteABTest?.attributes.alternative;
  });

export const selectProfileBanners = createSelector(selectFeature, (state: AccountState) => {
  return state.profileBanners;
});

export const selectFinancialSituation = createSelector(selectFeature, (state: AccountState) => {
  return state.financialSituation;
});

export const selectFinancialDetails = createSelector(selectFeature, (state: AccountState) => {
  return state.financialSituation;
});

export const selectAccountInvestDetails = createSelector(
  selectCustomerAndCurrentAccount,
  selectFinancialDetails,
  ({ customer, currentAccount }, financialDetails) => {
    let stillPossibleToInvest = 0;

    if (currentAccount.kind === '3a') {
      const possibleToInvest: number =
        MAXIMUM_3A_CONTRIBUTION[new Date().getFullYear()][currentAccount.max_contribution_level] - currentAccount.deposit_this_year;
      stillPossibleToInvest = possibleToInvest < 0 ? 0 : possibleToInvest;
    }

    return {
      ownerName: `${customer.first_name} ${customer.last_name}`,
      appLocation: customer.app_location,
      kind: currentAccount.kind,
      iban: currentAccount.iban,
      depositQrCode: currentAccount.deposit_qr_code,
      ...(currentAccount.kind === 'kid' && { kidName: currentAccount.owner_name }),
      ...(currentAccount.kind === '3a' && {
        currency: currentAccount.currency,
        isPossibleToCreate3A: currentAccount.max_contribution_level !== 'transfer_only',
        stillPossibleToInvest,
        libertyAccount: {
          qrIban: currentAccount.liberty_account.qr_iban,
          referenceNumber: currentAccount.liberty_account.reference_number
        }
      }),
      ...(customer.app_location === 'de' &&
        financialDetails !== null && {
          financialDetails: {
            iban: `${financialDetails.customer_iban}`,
            bank: financialDetails.bank_detail
          }
        })
    } as AccountInvestDetails;
  }
);

export const selectSavingsPlanSocialProof = createSelector(selectFeature, (state: AccountState) => state.currentAccount.savings_plan_social_proof);

export const selectDeEve2OptInLoadState = createSelector(selectFeature, (state: AccountState) => state.deEve2OptInLoadState);

export const selectCustomerAndCurrentAccountWithFeatureFlag = (flag: FeatureFlags) =>
  createSelector(selectCustomer, selectCurrentAccount, selectAccountFeatureFlag(flag), (customer, currentAccount, hasFeatureFlag) => {
    return { customer, currentAccount, hasFeatureFlag };
  });
